// FR-19, FR-21: exactly what a schedule item's and a stay's own props each
// carry -- a labelled link, ordered by the owner's own `position`. The
// frontend derives nothing from `url` (no host, no icon, no label
// fallback): `label` is whatever the admin typed, rendered verbatim.
export interface LinkProp {
  label: string
  url: string
}

// FR-38: the one shared pill component behind all three surfaces this
// task's design names -- the day-page item row, a stay's drawer row
// (`impl-stay-pills`) and the admin day list's non-interactive preview.
// Three color treatments (`docs/design/links.html` Blocks 1, 2, 4):
// `accent` (live day-page item / admin list), `grey` (a past item or past
// stay, still a working link), `white` (a current stay's drawer, the
// tinted-container variant `stayLabels.map`'s own pill already uses).
export type LinkPillVariant = "accent" | "grey" | "white"

const PILL_CLASSES: Record<LinkPillVariant, string> = {
  accent:
    "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent/5 px-2.5 " +
    "py-1 text-[13px] font-medium leading-[18px] text-accent",
  grey:
    "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-gray-100 px-2.5 " +
    "py-1 text-[13px] font-medium leading-[18px] text-gray-500",
  white:
    "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-white px-2.5 py-1 " +
    "text-[13px] font-medium leading-[18px] text-accent",
}

// The pill's own external-link glyph (`docs/design/links.html` Blocks 1,
// 2, 4), decorative in every surface -- `aria-hidden` regardless of
// whether the pill itself is a link or a preview span.
function ExternalIcon() {
  return (
    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path
        d="M4 2.5h5.5V8M9.5 2.5L2.5 9.5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

interface PillProps {
  link: LinkProp
  variant: LinkPillVariant
  interactive: boolean
}

// FR-34: the admin day list previews every pill as a non-interactive
// `span` (the whole row is its own tap target) -- the same `ArrowLink`
// idiom `Day.tsx` already uses for an interactive-vs-not element pair.
// Every other surface renders a real, working anchor (NFR-4: the same
// target/rel treatment the pre-t018 single pill already had).
function Pill({ link, variant, interactive }: PillProps) {
  const className = PILL_CLASSES[variant]

  if (!interactive) {
    return (
      <span className={className}>
        {link.label}
        <ExternalIcon />
      </span>
    )
  }

  return (
    <a href={link.url} target="_blank" rel="noopener noreferrer" className={className}>
      {link.label}
      <ExternalIcon />
    </a>
  )
}

interface LinkPillsProps {
  links: LinkProp[]
  variant: LinkPillVariant
  // Defaults to a real, working anchor -- only the admin day list preview
  // (FR-34) passes `interactive={false}`.
  interactive?: boolean
  // The row's own top margin, the one placement detail that differs
  // between surfaces: `mt-[3px]` (the default) is the day-page item row
  // and the admin day list (`docs/design/links.html` Blocks 1, 4);
  // `impl-stay-pills` passes the drawer's own `mt-0.5` (Block 2).
  marginClassName?: string
}

// FR-22, FR-23, FR-27, FR-28, FR-34: one wrapping row, `position` order
// (the order `links` already arrives in -- nothing here sorts), zero
// links rendering no row element at all -- not an empty container, not a
// residual gap.
export default function LinkPills({
  links,
  variant,
  interactive = true,
  marginClassName = "mt-[3px]",
}: LinkPillsProps) {
  if (links.length === 0) return null

  return (
    <div className={`${marginClassName} flex flex-wrap gap-1.5`}>
      {links.map((link, index) => (
        <Pill key={index} link={link} variant={variant} interactive={interactive} />
      ))}
    </div>
  )
}
